import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ChevronRight, ArrowRight, Award, BookOpen, Target, TrendingUp, ShieldCheck } from "lucide-react";

const FadeIn = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const CoachSachinPage = () => {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#070B14] text-white">
      {/* Sticky nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#070B14]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5" data-testid="coach-page-logo">
            <img src="/paladin-logo.jpeg" alt="Paladin" className="w-9 h-9 rounded-lg object-contain" />
            <div>
              <span className="font-bold text-sm tracking-wider text-white block leading-none">PALADIN</span>
              <span className="text-[8px] text-white/40 tracking-[0.15em] uppercase">Health & Human Performance</span>
            </div>
          </button>
          <button onClick={() => navigate("/")} className="text-white/50 hover:text-white text-sm transition-colors flex items-center gap-1.5">
            <ChevronRight className="w-4 h-4 rotate-180" /> Back
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 max-w-4xl mx-auto px-6">
        <FadeIn>
          <p className="text-[10px] tracking-[0.3em] uppercase text-blue-400 font-bold mb-6">Why Paladin</p>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-6">
            25 years of study.<br />22 years of coaching.<br />
            <span className="text-blue-400">One system that's followed athletes from youth to college.</span>
          </h1>
          <p className="text-white/60 text-base sm:text-lg max-w-2xl leading-relaxed">
            Paladin isn't an app with a workout library. It's the system I've spent my career building, refining with elite athletes, and now putting in your hands.
          </p>
        </FadeIn>
      </section>

      {/* The story */}
      <section className="border-y border-white/5 bg-[#0A0E18]/60 py-20">
        <div className="max-w-3xl mx-auto px-6">
          <FadeIn>
            <p className="text-xs tracking-[0.3em] uppercase text-white/30 font-semibold mb-4">The story</p>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-8">Coach Sachin McDonnell</h2>
            <div className="space-y-5 text-white/60 text-base sm:text-lg leading-relaxed">
              <p>I started studying training science at 17. By 20, I was coaching kids in their first competitive sports. Twenty-two years later, some of those same kids are college athletes — and the framework I used with them at age 12 is the same one we still use today, evolved across thousands of training sessions.</p>
              <p>The system is grounded in a body of research most athletes never hear about: <span className="text-white font-semibold">Long-Term Athletic Development.</span> It's the model elite national programs use to build athletes from age 6 to retirement. It's been around for decades. It works.</p>
              <p>Most apps don't use it because their business model rewards short-term hype, not long-term results. <span className="text-white font-semibold">Paladin is what happens when you build the opposite.</span></p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* The science — LTAD */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <FadeIn>
          <p className="text-xs tracking-[0.3em] uppercase text-emerald-400 font-semibold mb-4">The science</p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-3">Long-Term Athletic Development.</h2>
          <p className="text-white/40 text-sm sm:text-base mb-12 max-w-2xl leading-relaxed">
            A 40-year body of research on how athletes actually develop. Three names matter most:
          </p>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { name: "István Balyi", role: "LTAD Model", desc: "Mapped the seven stages from FUNdamentals to Active for Life. Used by national sport bodies in Canada, the UK, and Australia." },
            { name: "Tudor Bompa", role: "Periodization", desc: "Father of modern periodization theory. Defined the macrocycle/mesocycle/microcycle structure that every serious training program inherits from." },
            { name: "Yuri Verkhoshansky", role: "Block Periodization", desc: "Soviet sport science legend. Pioneered shock methods, plyometric progression, and the idea that training adaptations should sequence — not stack." },
          ].map((s, i) => (
            <FadeIn key={i} delay={i * 0.1}>
              <div className="bg-[#0f172a] border border-white/10 rounded-2xl p-5 h-full">
                <p className="text-blue-400 text-[10px] uppercase tracking-widest font-bold mb-2">{s.role}</p>
                <h3 className="text-white font-black mb-3">{s.name}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{s.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* The 5S framework */}
      <section className="bg-[#0A0E18] py-20">
        <div className="max-w-5xl mx-auto px-6">
          <FadeIn>
            <p className="text-xs tracking-[0.3em] uppercase text-amber-400 font-semibold mb-4">The 5S framework</p>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-3">Five biomotor abilities. Match the demand. Build what's missing.</h2>
            <p className="text-white/40 text-sm sm:text-base mb-12 max-w-2xl leading-relaxed">
              Every Paladin program starts with a SWOT analysis: what does the athlete's sport demand, and where are the gaps? Then we build across all five qualities — never just one.
            </p>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { title: "SPEED", desc: "How fast you produce force. Sprint mechanics, acceleration, max velocity.", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
              { title: "STRENGTH", desc: "Force production. The base every other quality is built on.", color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20" },
              { title: "STAMINA", desc: "Sport-specific endurance. Your engine, tuned to your event.", color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
              { title: "SUPPLENESS", desc: "Mobility through the full range. Injury prevention as performance, not afterthought.", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
              { title: "SKILL", desc: "Where the other four become a sprint, a jump, a change of direction. The expression.", color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/20" },
            ].map((pillar, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div className={`${pillar.bg} border ${pillar.border} rounded-2xl p-5 h-full`}>
                  <h3 className={`text-sm font-black tracking-wider mb-3 ${pillar.color}`}>{pillar.title}</h3>
                  <p className="text-white/50 text-xs leading-relaxed">{pillar.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* The methodology — Auto-progression / no junk reps */}
      <section className="max-w-3xl mx-auto px-6 py-24">
        <FadeIn>
          <p className="text-xs tracking-[0.3em] uppercase text-blue-400 font-semibold mb-4">The methodology</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-snug mb-10">
            How a 22-year coach lives inside an app.
          </h2>
        </FadeIn>

        <div className="space-y-6">
          {[
            { Icon: TrendingUp, title: "Auto-progression that respects the nervous system", body: "Loads adjust based on strength relative to bodyweight. As you get stronger, you need fewer reps — not more. The system bumps your max from your last session's feedback automatically. No spreadsheets. No CNS overload. No injuries from chasing volume that isn't earning anything." },
            { Icon: Target, title: "Vertically Integrated Periodization (VIP)", body: "Every mesocycle stacks on the last in a coherent progression. Speed feeds strength feeds power feeds skill. We don't bolt qualities onto random weeks — we sequence them the way Verkhoshansky said to, the way modern Olympic programs do." },
            { Icon: ShieldCheck, title: "The 5S SWOT", body: "Each program identifies the demands of your sport (or your goal), measures where you are across all five biomotor qualities, and prescribes against the gaps. The plan is never generic — it's specific to what you actually need." },
            { Icon: Award, title: "Athletic Lifespan continuity", body: "The same system follows an athlete from age 8 to 28. Different stages get different programming, but the framework never breaks. Your 12-year-old's program and your college athlete's program speak the same language." },
            { Icon: BookOpen, title: "Continuous evolution", body: "The version you're using has been refined with my elite athletes for two decades. New science gets tested with them first. When something proves itself, it ships to everyone. The platform grows with the science." },
          ].map(({ Icon, title, body }, i) => (
            <FadeIn key={i} delay={i * 0.05}>
              <div className="border-l-2 border-blue-500/30 pl-5 py-2">
                <div className="flex items-center gap-3 mb-2">
                  <Icon className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <h3 className="text-white font-bold text-base">{title}</h3>
                </div>
                <p className="text-white/55 text-sm leading-relaxed">{body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* The promise */}
      <section className="border-y border-white/5 bg-[#0A0E18] py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <FadeIn>
            <p className="text-xs tracking-[0.3em] uppercase text-amber-400 font-semibold mb-6">The promise</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-snug mb-8">
              Zero junk reps.<br /><span className="text-white/60">Every set is for a reason.</span>
            </h2>
            <p className="text-white/55 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
              If a rep doesn't move the needle on speed, strength, stamina, suppleness, or skill — it shouldn't be in your program. We've spent 22 years cutting what doesn't matter. What's left is what works.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 py-28 text-center">
        <FadeIn>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight mb-6">
            Ready to train<br /><span className="text-blue-400">on a real system?</span>
          </h2>
          <p className="text-white/50 text-base max-w-lg mx-auto mb-10 leading-relaxed">
            Find the path that fits your life — Foundations for sustainable progression, Accelerator for the all-in athlete.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={() => navigate("/")} className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-3.5 rounded-xl transition-colors text-sm tracking-wide flex items-center gap-2" data-testid="coach-page-cta-find">
              Find Your Program <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => navigate("/login")} className="text-white/40 hover:text-white/70 text-sm transition-colors">
              Already a member? Sign in
            </button>
          </div>
        </FadeIn>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/paladin-logo.jpeg" alt="" className="w-7 h-7 rounded object-contain" />
            <span className="text-white/30 text-xs tracking-wider font-bold">PALADIN HHP</span>
          </div>
          <div className="flex items-center gap-6 text-white/30 text-xs">
            <button onClick={() => navigate("/legal/terms")} className="hover:text-white/60 transition-colors">Terms</button>
            <button onClick={() => navigate("/legal/privacy")} className="hover:text-white/60 transition-colors">Privacy</button>
            <button onClick={() => navigate("/login")} className="hover:text-white/60 transition-colors">Sign In</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CoachSachinPage;
